// Shared UI atoms for the admin portal. Ported from the design-handoff
// `admin-data.jsx` / `admin-shell.jsx`. Style tokens map onto the
// frontend-admin theme already defined in `index.html` so visual
// continuity with the agent / consumer apps is exact.

import type { CSSProperties, ReactNode } from "react";

/* ─── Status pill (editorial review state) ────────────────────────── */
export type ReviewStateKey =
  | "awaiting_review"
  | "in_review"
  | "changes_requested"
  | "approved"
  | "published"
  | "scheduled"
  | "draft"
  | "rejected"
  | "archived";

export const REVIEW_STATE: Record<ReviewStateKey, { label: string; bg: string; ink: string; dot: string }> = {
  awaiting_review:   { label: "Awaiting review",   bg: "#f5ead1", ink: "#8a5a00", dot: "#b07c1a" },
  in_review:         { label: "In review",         bg: "#ddeaf5", ink: "#1a3e6a", dot: "#2a5b94" },
  changes_requested: { label: "Changes requested", bg: "#f1dcd4", ink: "#7a2d1f", dot: "#b04a32" },
  approved:          { label: "Approved",          bg: "#e0ebe2", ink: "#1d4a3a", dot: "#1d4a3a" },
  published:         { label: "Published",         bg: "#e0ebe2", ink: "#1d4a3a", dot: "#1d4a3a" },
  scheduled:         { label: "Scheduled",         bg: "#ddeaf5", ink: "#1a3e6a", dot: "#2a5b94" },
  draft:             { label: "Draft",             bg: "#efebe1", ink: "#5a4a20", dot: "#8a6f2a" },
  rejected:          { label: "Rejected",          bg: "#f1dcd4", ink: "#7a2d1f", dot: "#7a2d1f" },
  archived:          { label: "Archived",          bg: "#e6e1d5", ink: "#6e7974", dot: "#6e7974" },
};

export function ReviewPill({ state, size = "sm" }: { state: ReviewStateKey; size?: "sm" | "lg" }) {
  const t = REVIEW_STATE[state] ?? REVIEW_STATE.draft;
  const py = size === "lg" ? 4 : 2;
  const px = size === "lg" ? 11 : 9;
  const fs = size === "lg" ? 11.5 : 10.5;
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: `${py}px ${px}px`, borderRadius: 999,
        background: t.bg, color: t.ink,
        fontFamily: "var(--font-ui)", fontSize: fs, fontWeight: 600, letterSpacing: 0.2,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 5, height: 5, borderRadius: 999, background: t.dot }} />
      {t.label}
    </span>
  );
}

/* ─── Kind chip (URA / Policy / Market / Brief / Guide) ───────────── */
export type KindKey = "URA" | "Policy" | "Market" | "Brief" | "Guide";

const KIND_PALETTE: Record<KindKey, { bg: string; ink: string }> = {
  URA:    { bg: "#dfe7e0", ink: "#1d4a3a" },
  Policy: { bg: "#f0e7d0", ink: "#6b5320" },
  Market: { bg: "#d9e3eb", ink: "#2a4a66" },
  Brief:  { bg: "#dde2e1", ink: "#3d4a45" },
  Guide:  { bg: "#e8e4f8", ink: "#3a2e7a" },
};

export function KindChip({ kind }: { kind: KindKey | string }) {
  const t = KIND_PALETTE[kind as KindKey] ?? KIND_PALETTE.Brief;
  return (
    <span
      style={{
        padding: "2px 8px", borderRadius: 4,
        background: t.bg, color: t.ink,
        fontFamily: "var(--font-ui)", fontSize: 9.5, fontWeight: 700,
        letterSpacing: 0.8, textTransform: "uppercase", whiteSpace: "nowrap",
      }}
    >
      {kind}
    </span>
  );
}

/* ─── Author avatar ───────────────────────────────────────────────── */
export function Avatar({ initials, tint, size = 26, title }: { initials: string; tint?: string; size?: number; title?: string }) {
  return (
    <span
      title={title}
      style={{
        width: size, height: size, borderRadius: 999,
        background: tint ?? "var(--accent)", color: "#f4f1ea",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontFamily: "var(--font-ui)", fontSize: size * 0.38, fontWeight: 600,
        letterSpacing: 0.3, flexShrink: 0,
      }}
    >
      {initials}
    </span>
  );
}

/* ─── SLA badge ──────────────────────────────────────────────────── */
export function SlaBadge({ sla, age }: { sla: "overdue" | "on-time" | "cleared"; age?: string }) {
  const tone =
    sla === "overdue"
      ? { bg: "rgba(122,45,31,0.10)", fg: "#7a2d1f", label: age ?? "Overdue" }
      : sla === "on-time"
      ? { bg: "rgba(29,74,58,0.08)",  fg: "#1d4a3a", label: age ?? "On time" }
      : { bg: "var(--surface-2)",     fg: "var(--ink-3)", label: age ?? "—" };
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "2px 8px", borderRadius: 4,
        background: tone.bg, color: tone.fg,
        fontFamily: "var(--font-mono)", fontSize: 10.5, fontWeight: 500,
      }}
    >
      {tone.label}
    </span>
  );
}

/* ─── Generic badge for status / role chips ───────────────────────── */
export type BadgeTone = "pos" | "neg" | "warn" | "ai" | "info" | "mute";

export const BADGE_PALETTE: Record<BadgeTone, { bg: string; fg: string }> = {
  pos:  { bg: "rgba(29,74,58,0.10)",  fg: "#1d4a3a" },
  neg:  { bg: "rgba(122,45,31,0.10)", fg: "#7a2d1f" },
  warn: { bg: "rgba(138,90,0,0.12)",  fg: "#8a5a00" },
  ai:   { bg: "rgba(107,83,32,0.10)", fg: "#6b5320" },
  info: { bg: "rgba(26,62,106,0.10)", fg: "#1a3e6a" },
  mute: { bg: "var(--surface-2)",     fg: "var(--ink-3)" },
};

// `title` rides on hover (e.g. a failed send_error); children may be an icon
// glyph + label, so we lay them out inline-flex with a small gap.
export function Badge({ tone, title, children }: { tone: BadgeTone; title?: string; children: ReactNode }) {
  const p = BADGE_PALETTE[tone];
  return (
    <span
      title={title}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "2px 8px", borderRadius: 4,
        background: p.bg, color: p.fg,
        fontFamily: "var(--font-ui)", fontSize: 10.5, fontWeight: 600,
        letterSpacing: 0.06, textTransform: "uppercase", whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

/* ─── StatusDot — sync_runs status pill (success / running / error / skipped) */
export function StatusDot({ status }: { status: string }) {
  const tone =
    status === "success" ? { bg: "rgba(29,74,58,0.10)",  fg: "#1d4a3a" }
  : status === "running" ? { bg: "rgba(138,90,0,0.15)",  fg: "#8a5a00" }
  : status === "error"   ? { bg: "rgba(122,45,31,0.10)", fg: "#7a2d1f" }
  : status === "skipped" ? { bg: "var(--surface-2)",     fg: "var(--ink-4)" }
  :                        { bg: "var(--surface-2)",     fg: "var(--ink-3)" };
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "2px 8px", borderRadius: 4,
        background: tone.bg, color: tone.fg,
        fontFamily: "var(--font-ui)", fontSize: 10.5, fontWeight: 600,
        letterSpacing: 0.06, textTransform: "uppercase",
      }}
    >
      {status === "running" && (
        <span
          style={{
            width: 6, height: 6, borderRadius: 999, background: tone.fg,
            animation: "pulseOps 1.2s ease-in-out infinite",
          }}
        />
      )}
      {status}
    </span>
  );
}

/* ─── Page header (kicker / title / sub / actions) ────────────────── */
export function PageHeader({ kicker, title, sub, actions }: { kicker: string; title: string; sub?: string; actions?: ReactNode }) {
  return (
    <header
      style={{
        display: "flex", alignItems: "flex-end", justifyContent: "space-between",
        gap: 16, padding: "22px 26px 18px",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={pgKicker}>{kicker}</div>
        <h1 style={pgTitle}>{title}</h1>
        {sub && (
          <div
            style={{
              fontFamily: "var(--font-serif)", fontSize: 13.5, fontStyle: "italic",
              color: "var(--ink-3)", marginTop: 6, textWrap: "balance",
            } as CSSProperties}
          >
            {sub}
          </div>
        )}
      </div>
      {actions && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {actions}
        </div>
      )}
    </header>
  );
}

export const pgKicker: CSSProperties = {
  fontFamily: "var(--font-ui)", fontSize: 10.5, color: "var(--ink-4)",
  fontWeight: 600, letterSpacing: "0.13em", textTransform: "uppercase",
};
export const pgTitle: CSSProperties = {
  fontFamily: "var(--font-serif)", fontSize: 30, fontWeight: 500,
  letterSpacing: "-0.025em", margin: "6px 0 0", color: "var(--ink)",
  lineHeight: 1.1,
};
export const pgKickerStat: CSSProperties = {
  fontFamily: "var(--font-ui)", fontSize: 10, color: "var(--ink-4)",
  letterSpacing: "0.13em", textTransform: "uppercase", fontWeight: 600,
};

/* ─── Breadcrumbs ────────────────────────────────────────────────── */
export function Crumbs({ items }: { items: string[] }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, fontFamily: "var(--font-ui)" }}>
      {items.map((it, i) => (
        <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              fontSize: 12,
              color: i === items.length - 1 ? "var(--ink)" : "var(--ink-3)",
              fontWeight: i === items.length - 1 ? 500 : 400,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}
          >
            {it}
          </span>
          {i < items.length - 1 && (
            <span style={{ fontSize: 11, color: "var(--ink-4)" }}>›</span>
          )}
        </span>
      ))}
    </div>
  );
}

/* ─── Button atoms ───────────────────────────────────────────────── */
export const ghostBtn: CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "7px 12px", borderRadius: 7,
  background: "var(--surface)", border: "1px solid var(--rule)",
  color: "var(--ink-2)", cursor: "pointer",
  fontFamily: "var(--font-ui)", fontSize: 12, fontWeight: 500,
};
export const primaryBtn: CSSProperties = {
  ...ghostBtn, background: "var(--accent)", color: "#f4f1ea", border: "none",
};
export const smallBtn: CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 5,
  padding: "4px 10px", borderRadius: 5,
  background: "var(--surface)", border: "1px solid var(--rule)",
  color: "var(--ink-2)", cursor: "pointer",
  fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: 500,
};

/* ─── Card surface ───────────────────────────────────────────────── */
export const cardWrap: CSSProperties = {
  margin: "0 26px", borderRadius: 10,
  border: "1px solid var(--rule)", background: "var(--surface)",
  overflow: "hidden",
};
export const hdrCell: CSSProperties = {
  fontFamily: "var(--font-ui)", fontSize: 9.5, fontWeight: 600,
  letterSpacing: "0.11em", textTransform: "uppercase", color: "var(--ink-4)",
};

/* ─── One-time keyframes + row hover (injected once on import) ────── */
if (typeof document !== "undefined" && !document.getElementById("admin-ops-styles")) {
  const s = document.createElement("style");
  s.id = "admin-ops-styles";
  s.textContent = `
    @keyframes pulseOps { 0%,100% { opacity: 1 } 50% { opacity: 0.35 } }
    .ops-row { transition: background 120ms; cursor: pointer; }
    .ops-row:hover { background: var(--surface-2) !important; }
  `;
  document.head.appendChild(s);
}

/* ─── Inline SVG icon set (subset from admin-data.jsx) ────────────── */
const Ico = ({ d, size = 14, fill = "none", stroke = "currentColor", sw = 1.6 }: {
  d: ReactNode; size?: number; fill?: string; stroke?: string; sw?: number;
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={stroke}
       strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">{d}</svg>
);

export const Icon = {
  search:   <Ico d={<><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></>}/>,
  bell:     <Ico d={<><path d="M6 8a6 6 0 0 1 12 0c0 7 3 8 3 8H3s3-1 3-8"/><path d="M10 21a2 2 0 0 0 4 0"/></>}/>,
  filter:   <Ico d={<path d="M3 5h18l-7 9v6l-4-2v-4z"/>}/>,
  plus:     <Ico d={<path d="M12 5v14M5 12h14"/>}/>,
  arrowL:   <Ico d={<path d="M19 12H5M11 6l-6 6 6 6"/>}/>,
  arrowR:   <Ico d={<path d="M5 12h14M13 6l6 6-6 6"/>}/>,
  check:    <Ico d={<path d="M4 12l5 5L20 6"/>}/>,
  x:        <Ico d={<path d="M6 6l12 12M18 6 6 18"/>}/>,
  eye:      <Ico d={<><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></>}/>,
  cal:      <Ico d={<><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></>}/>,
  send:     <Ico d={<path d="M22 2 11 13M22 2l-7 20-4-9-9-4z"/>}/>,
  more:     <Ico d={<><circle cx="5" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="19" cy="12" r="1.5"/></>} fill="currentColor" stroke="none"/>,
  flag:     <Ico d={<path d="M4 22V4M4 4h11l-2 4 2 4H4"/>}/>,
  history:  <Ico d={<><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5M12 7v5l3 2"/></>}/>,
  link:     <Ico d={<><path d="M10 14a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1"/><path d="M14 10a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1"/></>}/>,
  pencil:   <Ico d={<path d="M12 20h9M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4z"/>}/>,
  alert:    <Ico d={<><path d="M12 9v4M12 17h.01"/><path d="M10.3 3.7 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.7a2 2 0 0 0-3.4 0z"/></>}/>,
  archive:  <Ico d={<><rect x="3" y="4" width="18" height="4" rx="1"/><path d="M5 8v12h14V8M10 12h4"/></>}/>,
};
